// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

//
// V11 — signing-method chooser for the /v/{id} pre-gate shell.
//
// Replaces the single "Prove ownership" button with a 2×2 tile grid
// that makes the plurality of signing providers explicit. Mirrors the
// mental model of user-app's home-page login chooser (F14.14) — some
// active tiles today, some greyed placeholders showing the direction
// of travel.
//
// Four tiles:
//   1. Cobault app  — active (default-highlighted). Cobault self-custody
//      via the V08b cross-origin popup flow.
//   2. Recovery     — active, de-emphasised. Stateless seed-phrase signing.
//   3. Hardware wallet  — active. NATIVE WebHID signing in THIS origin
//      (HW.12): the user connects a Ledger here and BSM-signs a
//      `bsm-bip137-v1` ownership proof on-device, which PreGateShell routes
//      to `/prove-v2` — no popup, no user-app dependency (arm's-length,
//      kickoff decision 1). HW.11 first rode the cobault-app popup; the
//      dedicated tile now does the independent thing. (BitBox02 is a
//      post-gate fast-follow, HW.15.)
//   4. Web3Auth / WaaS  — active. Native social-login signing in this
//      origin (no popup); replaces the broken cross-origin Web3Auth path.
//
// The chooser itself is stateless — the parent (PreGateShell) owns
// the selection and renders the chosen method's flow inline after
// the tiles collapse. A "Change method" link returns to the tile
// grid.
//

export type SigningMethod = 'cobault-app' | 'recovery' | 'hardware' | 'web3auth';

/** Tile descriptor used by the chooser. Kept inline rather than in a
 *  data module because the set is small and the React pattern for
 *  disabled tiles reads more clearly with literal JSX. */
interface TileDef {
  method: SigningMethod;
  title: string;
  subtitle: string;
  disabled: boolean;
  /** Rendered as a small pill in the tile corner; undefined → no pill. */
  badge?: string;
}

const TILES: readonly TileDef[] = [
  {
    method: 'cobault-app',
    title: 'Cobault app',
    subtitle:
      'Sign in a Cobault popup with your Cobault self-custody login. Your keys stay where you keep them.',
    disabled: false,
    badge: 'Recommended',
  },
  {
    method: 'recovery',
    title: 'Recovery with seed phrase',
    subtitle:
      'For when the Cobault app is unavailable. Enter your seed once; nothing is stored here.',
    disabled: false,
  },
  {
    method: 'hardware',
    title: 'Hardware wallet',
    subtitle:
      'Connect your Ledger and approve on-device, right here. No popup and no Cobault app needed — independent from start to finish.',
    disabled: false,
  },
  {
    method: 'web3auth',
    title: 'Web3Auth / WaaS',
    subtitle:
      'Sign in with your Web3Auth social login. Your key is reconstructed in your browser and signs here — nothing is stored.',
    disabled: false,
  },
];

export function SigningMethodChooser({
  onChoose,
}: {
  onChoose: (method: SigningMethod) => void;
}) {
  return (
    <div class="chooser-grid" role="radiogroup" aria-label="Signing method">
      {TILES.map((tile) => (
        <ChooserTile
          key={tile.method}
          tile={tile}
          onChoose={onChoose}
        />
      ))}
    </div>
  );
}

function ChooserTile({
  tile,
  onChoose,
}: {
  tile: TileDef;
  onChoose: (method: SigningMethod) => void;
}) {
  const { method, title, subtitle, disabled, badge } = tile;
  // `cobault-app` is the recommended path and gets a subtle emphasis
  // via CSS; all other active tiles are plain.
  const recommended = method === 'cobault-app';
  return (
    <button
      type="button"
      class="chooser-tile"
      data-method={method}
      data-recommended={recommended ? 'true' : undefined}
      disabled={disabled}
      aria-disabled={disabled ? 'true' : undefined}
      onClick={() => {
        if (disabled) return;
        onChoose(method);
      }}
    >
      <div class="chooser-tile-header">
        <span class="chooser-tile-title">{title}</span>
        {badge && (
          <span
            class="chooser-tile-badge"
            data-kind={disabled ? 'muted' : 'recommended'}
          >
            {badge}
          </span>
        )}
      </div>
      <div class="chooser-tile-subtitle">{subtitle}</div>
    </button>
  );
}
